import React from 'react'

import { useTheme } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import MuCard, { CardProps as MuCardProps } from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import getFlag from 'utils/getFlag'

/** IPv4 dotted or IPv6 (contains ":") — used for smaller monospace label */
const looksLikeIpAddress = (s?: string) => {
  if (!s || !s.trim()) return false
  if (s.includes(':')) return true
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(s.trim())
}

const looksLikeEmail = (s?: string) => Boolean(s && s.includes('@'))

/**
 * Avatar text from `initialsFrom`: multi-word names use first letter of each
 * word (e.g. "Random Violet Slug" → "RV"); single word uses first two letters;
 * otherwise two alphanumeric chars (e.g. IP fragment). Falls back to `username`.
 */
const getAvatarInitials = (initialsFrom?: string, username?: string) => {
  if (initialsFrom) {
    const t = initialsFrom.trim()
    if (t.includes(' ')) {
      const letters = t.match(/\b\w/g) || []
      return letters.join('').slice(0, 2).toUpperCase()
    }
    const compact = initialsFrom.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2)
    if (compact.length >= 2) return compact.toUpperCase()
    if (compact.length === 1) return `${compact}${compact}`.toUpperCase()
  }
  const source = username || ''
  return (source.match(/\b(\w)/g) || ['R', 'A'])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export type CardProps = MuCardProps & {
  username?: string
  /** Shown on second row when `countryCode` is not set (e.g. Storybook) */
  ipaddress?: string
  selected?: boolean
  /** Preferred source for avatar letters (e.g. visitor display name, or IP hex) */
  initialsFrom?: string
  /** Second row: flag + country code (e.g. US, PR) */
  countryCode?: string
  /** Second row, after the country (e.g. a "New" tag) */
  badge?: React.ReactNode
}

const ActivityCard = ({
  selected,
  username,
  ipaddress,
  initialsFrom,
  countryCode,
  badge,
  ...cardProps
}: CardProps) => {
  const theme = useTheme()
  const primaryIsIp = looksLikeIpAddress(username)
  const primaryIsEmail = looksLikeEmail(username)
  const avatarInitials = getAvatarInitials(initialsFrom, username)
  return (
    <MuCard
      variant="outlined"
      {...cardProps}
      sx={{
        cursor: 'pointer',
        width: '240px',
        height: '56px',
        borderRadius: '8px',
        borderColor: selected
          ? theme.palette.primary.dark
          : theme.palette.primary.light,
        backgroundColor: selected
          ? 'var(--mui-palette-primary-lighter)'
          : theme.palette.primary.contrastText,
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }}>
      <CardContent
        sx={{
          flex: '1',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          py: '10px !important',
          px: '12px !important',
        }}>
        <Avatar
          sx={{
            bgcolor: 'text.primary',
            width: 30,
            height: 30,
            fontSize: '0.7rem',
          }}>
          {avatarInitials}
        </Avatar>
        <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
          <Typography
            title={primaryIsIp ? username : undefined}
            sx={{
              fontSize: primaryIsIp ? '0.6875rem' : '0.8125rem',
              fontWeight: primaryIsIp ? 500 : primaryIsEmail ? 400 : 600,
              lineHeight: primaryIsIp ? '16px' : '18px',
              fontFamily: primaryIsIp
                ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                : undefined,
              letterSpacing: primaryIsIp ? '-0.01em' : undefined,
            }}
            color="text.primary"
            noWrap>
            {username}
          </Typography>
          {countryCode ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0 }}>
              <img
                alt=""
                src={getFlag(countryCode)}
                style={{
                  width: 16,
                  height: 12,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  lineHeight: '16px',
                }}
                color="var(--mui-palette-secondary-300)"
                noWrap>
                {countryCode.toUpperCase()}
              </Typography>
              {badge}
            </Stack>
          ) : ipaddress ? (
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 400,
                lineHeight: '16px',
              }}
              color="var(--mui-palette-secondary-300)"
              noWrap>
              {ipaddress}
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </MuCard>
  )
}

export default ActivityCard
